<script setup lang="ts">
import { useSnackStore } from "@/stores/snack";
import { emptyVFormFieldOption, type VFormFieldChoice, type VFormFieldOption } from "@/utils/DjangoTypes";
import { DefaultServerTableParams, type DynamicItemsService, type ServerTableParamsType } from "@/utils/TableUtils";
import { i18n } from "@/locales";
import { cloneDeep, debounce } from "lodash-es";
import { computed, onMounted, ref, toRef, watch, watchEffect } from "vue";

const snackStore = useSnackStore();

const props = defineProps<{
  modelValue: any;
  options?: VFormFieldOption;
  name?: string;
  label?: string;
  items?: VFormFieldChoice<string | number | boolean>[];
  service?: DynamicItemsService;
  multiple?: boolean;
  compact?: boolean;
  disableZero?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  loading?: boolean;
  fieldClass?: string;
  tableParams?: Partial<ServerTableParamsType>;
  change?: (index: unknown) => void;
  subtitleFn?: (srcItem: unknown) => string;
}>();

const emit = defineEmits(["update:modelValue", "update:selectedItems"]);

const { t } = i18n.global;
const defaultPrompt = t("common.promptSelect");

const options = props.options || emptyVFormFieldOption.value;
const fieldLabel = props.label ?? options.label;
const fieldChoices = ref<VFormFieldChoice<string | number | boolean>[]>(props.items ?? options.choices);

const density = props.compact ? "compact" : "default";

const fieldClass = ref(props.fieldClass ?? "");

const disabledRef = toRef(props, "disabled");

const dynamicItems = ref<VFormFieldChoice<string | number | boolean>[]>([]);

const isLoadingRef = toRef(props, "loading");
const isLoading = ref(false);
watch(isLoadingRef, (value) => {
  isLoading.value = value;
});

const searchQuery = ref("");

const itemsWithDefault = computed(() => {
  let items: VFormFieldChoice<string | number | boolean>[] = props.service ? dynamicItems.value : fieldChoices.value;

  if (autocompleteModel.value === 0) {
    items = [{ title: defaultPrompt, subtitle: "", value: 0, disabled: true }, ...items];
  }

  return items;
});

const page = ref(1);
const hasMore = ref(true);

const loadDynamicItems = async (reset = false) => {
  if (!props.service || isLoading.value) return;
  isLoading.value = true;

  try {
    let newItems: VFormFieldChoice<string>[] = [];
    const params = {
      ...cloneDeep(DefaultServerTableParams),
      search: searchQuery.value.trim() || undefined,
      ...props.tableParams,
      page: reset ? 1 : page.value,
    };

    const response = await props.service.getAll(params);
    newItems = response.data.items.map((item: any) => ({
      title: item._selectable?.title ?? item.title ?? item.name ?? item.id ?? "-- neznámý --",
      subtitle: item._selectable?.subtitle ?? "",
      value: item.id,
      disabled: false,
      entity: item,
    }));

    const valuesToCheck = Array.isArray(autocompleteModel.value) ? autocompleteModel.value : [autocompleteModel.value];

    for (const val of valuesToCheck) {
      if (
        val &&
        newItems &&
        !newItems.some((item) => item.value === val) &&
        !dynamicItems.value.some((item) => item.value === val)
      ) {
        const currentValue = await props.service.get(val as number);
        const item = currentValue.data;
        newItems.push({
          title: item._selectable?.title ?? item.title ?? item.name ?? item.id ?? "-- neznámý --",
          subtitle: item._selectable?.subtitle ?? "",
          value: item.id,
          disabled: false,
          entity: item,
        });
      }
    }

    if (reset) {
      dynamicItems.value.splice(0, dynamicItems.value.length, ...newItems);
      page.value = 1;
    } else {
      dynamicItems.value.push(...newItems);
      page.value += 1;
    }

    hasMore.value = dynamicItems.value.length < response.data.total;
  } catch (error) {
    snackStore.showError(error);
  } finally {
    isLoading.value = false;
  }
};

const handleSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchQuery.value = target.value;
  debouncedHandleSearch();
};

const handleSearch = async () => {
  await loadDynamicItems(true);
};

const debouncedHandleSearch = debounce(handleSearch, 300);

const handleIntersect = async (isIntersecting: boolean) => {
  if (isIntersecting && hasMore.value) {
    await loadDynamicItems(false);
  }
};

watch(
  () => props.items,
  (newVal) => {
    if (!props.service && newVal) {
      fieldChoices.value = newVal ?? [];
    }
  }
);

onMounted(async () => {
  if (props.items) {
    dynamicItems.value = props.items;
  }
});

const autocompleteModel = ref(props.modelValue); // Předpokládáme, že props.modelValue je počáteční hodnota

watch(
  autocompleteModel,
  async (newValue, oldValue) => {
    if (oldValue !== newValue && props.service && !dynamicItems.value.some((item) => item.value === newValue)) {
      await loadDynamicItems(true);
    }
  },
  { immediate: true }
);

watchEffect(() => {
  autocompleteModel.value = props.modelValue;
});

watch(autocompleteModel, (newValue) => {
  emit("update:modelValue", newValue);
  emit(
    "update:selectedItems",
    dynamicItems.value.filter((item) => (Array.isArray(newValue) ? newValue.includes(item.value) : item.value === newValue))
  );
});
</script>

<template>
  <v-autocomplete
    v-model="autocompleteModel"
    :label="fieldLabel"
    :items="itemsWithDefault"
    :chips="props.multiple"
    :no-filter="!!props.service"
    persistent-placeholder
    :readonly="props.readonly"
    :multiple="props.multiple"
    :loading="isLoading"
    :disabled="disabledRef"
    :selectable="(option: any) => option.disabled"
    closable-chips
    :clearable="!props.readonly"
    hide-details="auto"
    variant="outlined"
    :density="density"
    :placeholder="defaultPrompt"
    :class="fieldClass"
    persistent
    @update:model-value="
      console.log('modelValue update', $event);
      emit('update:modelValue', $event);
    "
    @input="handleSearchInput"
  >
    <template #item="{ props, item }">
      <v-list-item
        v-bind="props"
        :disabled="item.raw.disabled"
        :subtitle="item.raw.subtitle"
      />
    </template>
    <template
      v-if="!!props.service"
      #append-item
    >
      <div v-intersect="handleIntersect"></div>
    </template>
    <template #append-inner>
      <slot name="append-inner"></slot>
    </template>
  </v-autocomplete>
</template>
